import { FsEvent } from '@fullstory/fs-script-api';
import { matchPath, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { SuggestionGroupType } from '@fullstory/proto/fs/services/alexa/suggest';
import { stringFromRole } from '@fullstory/util/roles';
import { useCallback, useMemo } from 'react';
import { createSelector } from 'reselect';
import { fullstorySelectors } from '@fullstory/redux';

export type FilterType = 'user' | 'event' | 'dependent';

export interface User {
  org_id_str: string;
  user_email_str: string;
  user_seat_type_str: string;
}

export interface Suggestion {
  // the type of suggestion, i.e. alexa.SuggestionType
  type_str: string;
  // the category of the suggestion, i.e. alexa.SuggestionGroupType
  category_str: string;
  filter_type_str?: FilterType;
  label_str: string;
  value_str?: string;
  entityType_str?: string;
}

export interface OmniboxBase {
  user: User;
  suggestion: Suggestion;
  version_str: 'react' | 'tomato';
  search_term_str: string;
  search_page_str?: string;
  group_view_str: string;
}

const { getOrgId, getSeatRole, getUserInfo } = fullstorySelectors;

const trackSuggestionSelected = (properties: OmniboxBase) => {
  FsEvent.recordCustomEvent('Omnibox Suggestion Selected', properties);
};

export const useOmniboxCustomEventFunc = () => {
  const location = useLocation();
  const match = useMemo(
    () =>
      matchPath<{ orgId: string; pageRoot: string }>(location.pathname, {
        path: '/ui/:orgId/:pageRoot',
      }),
    [location.pathname],
  );
  const user = useSelector(getUser);
  return useCallback(
    (term: string, groupView: SuggestionGroupType, s: Suggestion) =>
      trackSuggestionSelected({
        search_term_str: term,
        suggestion: s,
        group_view_str: SuggestionGroupType[groupView] ?? '',
        search_page_str: match?.params?.pageRoot,
        version_str: 'react',
        user,
      }),
    [user, match?.params?.pageRoot],
  );
};

const getUser = createSelector(
  [getOrgId, getUserInfo, getSeatRole],
  (orgId, userInfo, seatRole) => ({
    org_id_str: orgId,
    user_email_str: userInfo?.email,
    user_seat_type_str: stringFromRole(seatRole),
  }),
);
